import { Button, ToastCustom } from "@components";
import { Eraser } from "@untitledui/icons";
import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { cx } from "@/utils/cx";
import type { SignatureProps } from "../types";
import { ImagePreview } from "./";
import { LandscapeWrapper } from "./landscape";

const uiText = {
	title: "assinatura",
	signatureBtn: "Toque para assinar",
	required: "Obrigatório",
	submit: "Assinar documento",
};

const isLandscapeOrientation = () =>
	window.matchMedia?.("(orientation: landscape)").matches ||
	window.innerWidth > window.innerHeight;

export const SubmitReview = ({
	name = "signature",
	value = null,
	onChange,
	error = false,
}: SignatureProps) => {
	const containerRef = useRef<HTMLDivElement>(null);
	const canvasRef = useRef<HTMLCanvasElement>(null);
	const pointerIdRef = useRef<number | null>(null);

	const [isSigning, setIsSigning] = useState(false);
	const [isDrawing, setIsDrawing] = useState(false);
	const [isLandscape, setIsLandscape] = useState<boolean>(() =>
		typeof window !== "undefined" ? isLandscapeOrientation() : true
	);
	const [fullscreenFallback, setFullscreenFallback] = useState(false);
	const [shouldEnterFullscreen, setShouldEnterFullscreen] = useState(false);

	const disableFullscreenFallback = useCallback(() => {
		setFullscreenFallback(false);
		document.body.style.overflow = "initial";
		document.documentElement.style.overflow = "initial";
		document.body.style.height = "";
		document.documentElement.style.height = "";
	}, []);

	const enableFullscreenFallback = useCallback(() => {
		document.body.style.overflow = "hidden";
		document.documentElement.style.overflow = "hidden";
		setFullscreenFallback(true);
	}, []);

	const exitFullscreen = useCallback(async () => {
		if (document.fullscreenElement) {
			await document.exitFullscreen?.();
		}
		disableFullscreenFallback();
	}, [disableFullscreenFallback]);

	const enterFullscreen = useCallback(async () => {
		const target = containerRef.current ?? document.documentElement;

		// Tipos para compatibilidade com diferentes navegadores
		type FullscreenDocument = Document & {
			webkitFullscreenElement?: Element | null;
			mozFullScreenElement?: Element | null;
			msFullscreenElement?: Element | null;
		};

		type FullscreenElement = HTMLElement & {
			webkitRequestFullscreen?: () => Promise<void>;
			mozRequestFullScreen?: () => Promise<void>;
			msRequestFullscreen?: () => Promise<void>;
		};

		const doc = document as FullscreenDocument;
		const element = target as FullscreenElement;

		// Verifica se já está em fullscreen
		if (
			document.fullscreenElement ||
			doc.webkitFullscreenElement ||
			doc.mozFullScreenElement ||
			doc.msFullscreenElement
		) {
			return;
		}

		try {
			// Tenta diferentes métodos de fullscreen para compatibilidade
			if (target.requestFullscreen) {
				await target.requestFullscreen();
			} else if (element.webkitRequestFullscreen) {
				// Chrome, Safari, Opera
				await element.webkitRequestFullscreen();
			} else if (element.mozRequestFullScreen) {
				// Firefox
				await element.mozRequestFullScreen();
			} else if (element.msRequestFullscreen) {
				// IE/Edge
				await element.msRequestFullscreen();
			} else {
				throw new Error("Fullscreen API not supported");
			}
			setFullscreenFallback(false);
		} catch {
			// Se falhar, usa fallback
			enableFullscreenFallback();
		}
	}, [enableFullscreenFallback]);

	useEffect(
		() => () => {
			exitFullscreen();
		},
		[exitFullscreen]
	);

	const initializeContext = useCallback(() => {
		const canvas = canvasRef.current;
		if (!canvas) {
			return null;
		}
		const ctx = canvas.getContext("2d");
		if (!ctx) {
			return null;
		}
		ctx.lineWidth = 2.5;
		ctx.lineJoin = "round";
		ctx.lineCap = "round";
		ctx.strokeStyle = "#111827";
		return ctx;
	}, []);

	const resizeCanvas = useCallback(() => {
		const canvas = canvasRef.current;
		if (!canvas || typeof window === "undefined") {
			return;
		}

		const parentElement = canvas.parentElement;
		if (!parentElement) {
			return;
		}

		const parentWidth = parentElement.clientWidth;
		const parentHeight = parentElement.clientHeight;
		const dpr = window.devicePixelRatio || 1;
		const previousImage = canvas.toDataURL("image/png");

		canvas.style.width = `${parentWidth}px`;
		canvas.style.height = `${parentHeight}px`;
		canvas.width = Math.floor(parentWidth * dpr);
		canvas.height = Math.floor(parentHeight * dpr);

		const ctx = initializeContext();
		if (!ctx) {
			return;
		}
		ctx.setTransform(1, 0, 0, 1, 0, 0);
		ctx.scale(dpr, dpr);
		ctx.lineWidth = 2.5;
		ctx.lineJoin = "round";
		ctx.lineCap = "round";
		ctx.strokeStyle = "#111827";

		if (previousImage) {
			const img = new Image();
			img.onload = () => {
				ctx.drawImage(img, 0, 0, parentWidth, parentHeight);
			};
			img.src = previousImage;
		}
	}, [initializeContext]);

	useEffect(() => {
		if (!isSigning) {
			return;
		}
		resizeCanvas();
	}, [isSigning, resizeCanvas]);

	useEffect(() => {
		if (typeof window === "undefined") {
			return;
		}

		const handleOrientationChange = () => {
			const landscape = isLandscapeOrientation();
			setIsLandscape(landscape);
		};

		// Atualiza imediatamente
		handleOrientationChange();

		// Escuta mudanças de orientação
		const mediaQuery = window.matchMedia("(orientation: landscape)");
		mediaQuery.addEventListener("change", handleOrientationChange);

		// Escuta mudanças de tamanho da janela
		window.addEventListener("resize", handleOrientationChange);

		// Escuta evento específico de mudança de orientação (mobile)
		window.addEventListener("orientationchange", () => {
			// Pequeno delay para garantir que a orientação foi atualizada
			setTimeout(handleOrientationChange, 100);
		});

		return () => {
			mediaQuery.removeEventListener("change", handleOrientationChange);
			window.removeEventListener("resize", handleOrientationChange);
			window.removeEventListener("orientationchange", handleOrientationChange);
		};
	}, []);

	useEffect(() => {
		if (!(isSigning && isLandscape)) {
			return;
		}
		const handleResize = () => {
			resizeCanvas();
		};
		window.addEventListener("resize", handleResize);
		window.addEventListener("orientationchange", handleResize);

		// Observa mudanças no tamanho do container do canvas
		const canvasContainer = canvasRef.current?.parentElement;
		if (canvasContainer) {
			const resizeObserver = new ResizeObserver(() => {
				resizeCanvas();
			});
			resizeObserver.observe(canvasContainer);
			return () => {
				window.removeEventListener("resize", handleResize);
				window.removeEventListener("orientationchange", handleResize);
				resizeObserver.disconnect();
			};
		}

		return () => {
			window.removeEventListener("resize", handleResize);
			window.removeEventListener("orientationchange", handleResize);
		};
	}, [isSigning, resizeCanvas, isLandscape]);

	useEffect(() => {
		if (!isSigning) {
			return;
		}
		if (!isLandscape) {
			setShouldEnterFullscreen(true);
			exitFullscreen();
			return;
		}
		if (shouldEnterFullscreen) {
			enterFullscreen().then(() => setShouldEnterFullscreen(false));
		}
	}, [
		enterFullscreen,
		exitFullscreen,
		isLandscape,
		isSigning,
		shouldEnterFullscreen,
	]);

	const getCoordinates = useCallback(
		(event: React.PointerEvent<HTMLCanvasElement>) => {
			const canvas = canvasRef.current;
			if (!canvas) {
				return null;
			}
			const rect = canvas.getBoundingClientRect();
			return {
				x: event.clientX - rect.left,
				y: event.clientY - rect.top,
			};
		},
		[]
	);

	const startDrawing = (event: React.PointerEvent<HTMLCanvasElement>) => {
		// Bloqueia completamente o desenho se não estiver em landscape
		if (!(isLandscape && isSigning)) {
			event.preventDefault();
			event.stopPropagation();
			return;
		}
		const ctx = initializeContext();
		const coords = getCoordinates(event);
		if (!(ctx && coords)) {
			return;
		}
		pointerIdRef.current = event.pointerId;
		canvasRef.current?.setPointerCapture(event.pointerId);
		ctx.beginPath();
		ctx.moveTo(coords.x, coords.y);
		setIsDrawing(true);
	};

	const draw = (event: React.PointerEvent<HTMLCanvasElement>) => {
		// Bloqueia completamente o desenho se não estiver em landscape
		if (
			!(isLandscape && isDrawing) ||
			pointerIdRef.current !== event.pointerId
		) {
			event.preventDefault();
			event.stopPropagation();
			return;
		}
		const ctx = initializeContext();
		const coords = getCoordinates(event);
		if (!(ctx && coords)) {
			return;
		}
		ctx.lineTo(coords.x, coords.y);
		ctx.stroke();
	};

	const stopDrawing = (event?: React.PointerEvent<HTMLCanvasElement>) => {
		if (pointerIdRef.current !== null) {
			canvasRef.current?.releasePointerCapture(pointerIdRef.current);
		}
		if (event) {
			event.preventDefault();
		}
		setIsDrawing(false);
		pointerIdRef.current = null;
	};

	const clearSignature = useCallback(() => {
		const canvas = canvasRef.current;
		if (!canvas) {
			return;
		}
		const ctx = initializeContext();
		if (!ctx) {
			return;
		}
		ctx.clearRect(0, 0, canvas.width, canvas.height);
		setIsDrawing(false);
	}, [initializeContext]);
	const saveSignature = useCallback(() => {
		const canvas = canvasRef.current;
		if (!canvas) {
			onChange?.(null);
			setIsSigning(false);
			exitFullscreen();
			return;
		}

		// Verifica se o canvas tem algo desenhado
		const ctx = canvas.getContext("2d");
		if (!ctx) {
			onChange?.(null);
			setIsSigning(false);
			exitFullscreen();
			return;
		}

		const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
		const hasDrawing = imageData.data.some((channel) => channel !== 0);

		if (!hasDrawing) {
			toast(
				<ToastCustom
					description="Por favor, faça a assinatura antes de confirmar."
					title="Atenção"
				/>
			);
			return;
		}

		canvas.toBlob(
			(blob) => {
				if (!blob) {
					onChange?.(null);
					return;
				}
				const file = new File([blob], "signature.png", { type: "image/png" });
				const uri = URL.createObjectURL(file);
				const finalFile = Object.assign(file, { uri });
				onChange?.(finalFile);
			},
			"image/png",
			0.95
		);

		setIsSigning(false);
		setIsDrawing(false);
		pointerIdRef.current = null;
		exitFullscreen();
	}, [exitFullscreen, onChange]);

	const cancelSignature = useCallback(() => {
		setIsSigning(false);
		setIsDrawing(false);
		pointerIdRef.current = null;

		onChange?.(null);

		exitFullscreen();
	}, [exitFullscreen, onChange]);

	const openSignature = async () => {
		setIsSigning(true);
		setShouldEnterFullscreen(true);

		// Espera o render do canvas antes de rolar pro topo
		setTimeout(() => {
			containerRef.current?.scrollIntoView({
				behavior: "smooth",
				block: "start",
			});
		}, 50);

		if (isLandscapeOrientation()) {
			await enterFullscreen();
			setShouldEnterFullscreen(false);
		}
	};

	// Sempre mostra o bloqueio quando não estiver em landscape E estiver assinando
	const isBlockingOrientation = !isLandscape && isSigning;

	return (
		<>
			{isSigning ? (
				<div
					className={`fixed inset-0 z-40 select-none ${
						fullscreenFallback ? "overflow-hidden" : ""
					}`}
					ref={containerRef}
				>
					<div className="flex h-full flex-col">
						<div className="flex-1 overflow-hidden bg-secondary">
							<div className="relative flex h-full w-full items-center justify-center">
								<div className="absolute bottom-2/5 left-0 z-0 h-px w-full bg-[#9CA3AF]" />
								<canvas
									className="relative z-10 h-full w-full"
									onPointerCancel={stopDrawing}
									onPointerDown={startDrawing}
									onPointerLeave={stopDrawing}
									onPointerMove={draw}
									onPointerUp={stopDrawing}
									ref={canvasRef}
									style={{
										touchAction: isLandscape ? "none" : "auto",
										pointerEvents: isLandscape ? "auto" : "none",
									}}
								/>
							</div>
						</div>
						<div className="flex shrink-0 flex-wrap items-center justify-end gap-3 border-t border-t-slate-200 bg-gray-50 p-4 pb-8 text-white shadow-footer">
							<div className="flex flex-wrap gap-2">
								<Button
									className="w-32"
									onClick={cancelSignature}
									type="button"
									variant="secondary"
								>
									Voltar
								</Button>
								<Button
									className="absolute top-6 right-6 z-10 size-10! w-fit rounded-lg"
									disabled={!isLandscape}
									onClick={clearSignature}
									type="button"
									variant="secondary"
								>
									<Eraser className="size-5 text-fg-quaternary" />
								</Button>
								<Button
									className="w-32"
									disabled={isBlockingOrientation}
									onClick={saveSignature}
									type="button"
								>
									Confirmar
								</Button>
							</div>
						</div>
					</div>
					{isBlockingOrientation ? (
						<LandscapeWrapper message="Use o modo horizontal para assinar o documento." />
					) : null}
				</div>
			) : (
				<div className="h-[456] w-full">
					<ImagePreview
						className={cx(
							"h-[120px]",
							!!value?.uri && "border-b border-b-[#9CA3AF]"
						)}
						error={error}
						icon="edit"
						label={uiText.signatureBtn}
						objectPosition="object-contain"
						onClick={() => openSignature()}
						previewUri={value?.uri}
					/>
					{name ? (
						<input name={name} type="hidden" value={value?.uri ?? ""} />
					) : null}
				</div>
			)}
		</>
	);
};
